import { ThemeOptions, Theme } from '@material-ui/core/styles/createTheme';
import cookie from 'cookie';
import { Overrides } from '@material-ui/core/styles/overrides';
import { LabComponentNameToClassKey } from '@material-ui/lab/themeAugmentation';
import { StyleRules } from '@material-ui/core/styles/withStyles';
import * as tokens from '@utmost.co/utmost-design-tokens';
import appTheme from './appTheme';

export const ELEVATION = {
  level0: {
    boxShadow: tokens.elevationLevel0 // --shadow-none
  },
  level1: {
    boxShadow: tokens.elevationLevel1 // --shadow-weak
  },
  level2: {
    boxShadow: tokens.elevationLevel2 // --shadow-moderate
  },
  level3: {
    boxShadow: tokens.elevationLevel3 // --shadow-strong
  },
  level4: {
    boxShadow: tokens.elevationLevel4 // --shadow-stronger
  },
  level5: {
    boxShadow: tokens.elevationLevel5 // --shadow-strongest
  }
};

export const theme: Partial<ThemeOptions> = {
  transitions: {
    create: (animation?: string | string[], options?: { duration?: number | string; delay?: number | string }) => {
      if (!animation) {
        if (cookie.parse(document.cookie).disableAnimations) return 'none';
        return animation!;
      }

      if (cookie.parse(document.cookie).disableAnimations) return 'none';

      if (typeof animation === 'string')
        return `${animation} ${options && options.duration ? options.duration : '0'}ms ease ${
          options && options.delay ? options.delay : '0'
        }ms`;

      let toRet = '';
      animation.forEach((anim) => {
        toRet = `${anim} ${options && options.duration ? options.duration : '0'}ms ease ${
          options && options.delay ? options.delay : '0'
        }ms`;
      });

      return toRet! as any;
    }
  },
  palette: {
    secondary: { main: tokens.source.colorSecondary },
    primary: { main: tokens.source.colorPrimary },
    error: { main: tokens.source.colorAlert }
  },
  props: {
    MuiButtonBase: {
      disableRipple: false
    }
  }
};

(theme.overrides as Overrides & LabOverrides) = {
  MuiSelect: {
    root: {
      height: 36,
      padding: 0
    },
    selectMenu: {
      borderRadius: 2,
      height: 34, // root height - borderRadius
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      paddingLeft: 8,
      backgroundColor: tokens.colorSurface,
      color: tokens.colorOnSurface,
      border: `1px solid ${tokens.colorSurfaceVariant}`,

      '&:hover': {
        borderColor: tokens.colorSurfaceVariant,
        '.clear-all': {
          visibility: 'visible'
        }
      },
      '&:focus': {
        background: 'transparent',
        '.clear-all': {
          visibility: 'visible'
        },
        borderColor: tokens.colorPrimary
      },
      '&$disabled': {
        backgroundColor: tokens.colorSurfaceVariant,
        borderColor: tokens.colorSurfaceVariant
      },
      '&[aria-valuetext=""]': {
        color: tokens.colorOnSurfaceVariant,
        fontSize: 13,
        opacity: 1,
        fontWeight: 500
      }
    }
  },
  MuiCard: {
    root: {
      backgroundColor: tokens.colorSurface,
      color: tokens.colorOnSurface
    }
  },
  MuiAutocomplete: {
    root: {
      '&[data-version="legacy"]': {
        transition: 'all 0.15s ease-in-out',
        minHeight: 36,
        padding: 0,
        paddingTop: 2,
        backgroundColor: tokens.colorSurface,
        color: tokens.colorOnSurface,
        border: `1px solid ${tokens.colorSurfaceVariant}`,
        '&:hover': {
          borderColor: tokens.colorSurfaceVariant
        },
        '&:focus': {
          background: 'transparent',
          borderColor: tokens.colorPrimary
        },
        '&:focus-within': {
          background: 'transparent',
          borderColor: tokens.colorPrimary
        },
        '&[aria-valuetext=""]': {
          color: tokens.colorOnSurfaceVariant,
          fontSize: 13,
          opacity: 1,
          fontWeight: 500
        },
        '&:has(.Mui-disabled), &:has(.Mui-disabled) .MuiInputBase-root, &:has(.Mui-disabled) .MuiInputBase-input': {
          backgroundColor: tokens.paletteNeutralVariant95
        },
        '& .MuiAutocomplete-clearIndicator.MuiAutocomplete-clearIndicatorDirty': {
          visibility: 'visible'
        },
        '& .MuiAutocomplete-popupIndicator': {
          padding: '12px'
        }
      }
    },
    option: {
      color: tokens.colorOnSurface
    },
    endAdornment: {
      top: `calc(50% - 24px)`
    },
    inputRoot: {
      paddingLeft: 8,
      '& .MuiAutocomplete-input': {
        minWidth: 120
      }
    },
    input: {
      paddingLeft: 0,
      borderRadius: 2,
      backgroundColor: tokens.colorSurface,
      color: tokens.colorOnSurface,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      alignItems: 'center',
      flexGrow: 1,
      'div + &': {
        paddingLeft: 8
      },
      '[data-test="selected-value"] + &': {
        paddingLeft: 0
      }
    }
  },
  MuiListItemIcon: {
    root: {
      minWidth: 24 + 8
    }
  },
  MuiListItem: {
    root: {
      '&.Mui-selected': {
        backgroundColor: '#e4f2ff'
      }
    }
  },
  MuiMenuItem: {
    root: {
      cursor: 'pointer',
      minHeight: 36,
      backgroundColor: tokens.colorSurface,
      color: tokens.colorOnSurface,
      fontSize: 14,
      boxSizing: 'border-box',
      alignItems: 'center',
      padding: '8px 16px',
      '&.Mui-selected': {
        backgroundColor: tokens.colorPrimaryContainer,
        color: tokens.colorOnPrimaryContainer
      },
      '&:hover': {
        backgroundColor: tokens.paletteNeutralVariant95
      },
      '&:active': {
        backgroundColor: tokens.paletteNeutralVariant95
      }
    }
  },
  MuiFormLabel: {
    root: {
      color: tokens.colorOnSurface,
      '&.Mui-disabled': {
        color: tokens.colorOnSurface
      }
    }
  },
  MuiInputBase: {
    root: {
      backgroundColor: tokens.colorSurface,
      color: tokens.colorOnSurface,
      transition: 'all 0.1s ease-in-out',
      boxSizing: 'border-box',
      fontSize: 13,
      fontWeight: 500,
      'label + .MuiInput-formControl&': {
        marginTop: 0
      },
      '& .clear-all': {
        visibility: 'hidden'
      },
      '&:hover': {
        '& .clear-all': {
          visibility: 'visible'
        }
      },
      '&:focus': {
        '& .clear-all': {
          visibility: 'hidden'
        }
      }
    },
    input: {
      paddingLeft: 8
    }
  },
  MuiPaper: {
    root: {
      backgroundColor: tokens.colorSurface,
      color: tokens.colorOnSurface,
      '&.MuiPopover-paper, &.MuiAutocomplete-paper': {
        ...ELEVATION.level2,
        borderRadius: tokens.radiusDefault,
        fontSize: appTheme.font.sizeSmall,
        border: tokens.colorSurfaceVariant
      }
    }
  },
  MuiPopover: {
    root: {
      '& .MuiInputBase-root': {
        backgroundColor: tokens.colorSurfaceVariant,
        borderColor: 'transparent'
      }
    }
  },
  MuiSkeleton: {
    root: {
      backgroundColor: tokens.colorSurfaceVariant
    }
  },
  MuiTextField: {
    root: {
      '&.select .MuiInput-root:not(.Mui-disabled), &.select .MuiInput-input:not(.Mui-disabled)': {
        cursor: 'pointer'
      }
    }
  },
  MuiInput: {
    root: {
      height: 36,
      padding: '8px 0 8px 0',
      borderRadius: 2,
      border: `1px solid ${tokens.colorSurfaceVariant}`,
      transition: 'all 0.1s ease-in-out',
      boxSizing: 'border-box',
      '&:hover': {
        borderColor: tokens.colorSurfaceVariant
      },
      '&$focused': {
        borderColor: tokens.colorPrimary
      },
      '&$disabled': {
        backgroundColor: tokens.paletteNeutralVariant95,
        borderColor: tokens.colorSurfaceVariant
      },
      '&$error': {
        borderColor: tokens.colorAlert,
        '&:hover': {
          borderColor: tokens.colorAlert
        },
        '&$focused': {
          borderColor: tokens.colorAlert
        }
      }
    },
    multiline: {
      height: 'auto',
      paddingLeft: 8
    },
    underline: {
      border: 'none'
    },
    input: {
      fontSize: 13,
      fontWeight: 500,
      minWidth: 120,

      '&::placeholder': {
        color: tokens.colorOnSurface,
        fontSize: 13,
        opacity: 0.38,
        fontWeight: 500
      }
    }
  },
  MuiInputLabel: {
    root: {
      color: tokens.colorOnSurface,
      fontWeight: 500,
      fontSize: 14,
      marginBottom: 8,
      '&$error': {
        color: tokens.colorAlert,
        fontSize: 14
      },
      '&.MuiInputLabel-formControl': {
        position: 'initial' // Override absolute position.
      }
    },
    asterisk: {
      color: tokens.colorAlert,
      '&$error': {
        color: tokens.colorAlert,
        fontSize: 14
      }
    },
    shrink: {
      color: tokens.colorOnSurface,
      fontSize: 14,
      fontWeight: 500,
      transform: 'none'
    }
  },
  MuiListItemText: {
    root: {
      '& .MuiListItemText-secondary': {
        color: tokens.colorOnSurface
      }
    }
  },
  MuiTabs: {
    root: {
      minHeight: '49px',
      '& .MuiTabs-indicator': {
        // reset MuiTabs indicator for better control
        height: '0px',
        backgroundColor: 'none'
      },
      '&.tab-variant': {
        borderBottom: `1px solid ${tokens.colorSurfaceVariant}`,
        '& .Mui-selected': {
          position: 'relative',
          zIndex: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: -1,
            left: 0,
            right: 0,
            bottom: 0,
            height: '4px',
            backgroundColor: tokens.colorPrimary,
            borderRadius: '4px 4px 0 0'
          }
        }
      },
      '&.nav-variant': {
        backgroundColor: tokens.colorSurface,
        color: tokens.colorOnSurface,
        fontSize: appTheme.font.sizeSmall,
        '& .MuiButtonBase-root': {
          minWidth: '90px',
          maxWidth: '360px',
          padding: `6px ${appTheme.spacingSize.l}`,
          borderRadius: tokens.radiusDefault,
          '&:hover': {
            backgroundColor: 'rgba(26, 26, 26, 0.08)'
          },
          '&:focus': {
            backgroundColor: 'rgba(26, 26, 26, 0.12)'
          },
          '&.Mui-selected': {
            backgroundColor: tokens.colorSecondaryContainer,
            color: tokens.colorOnSecondaryContainer,
            position: 'relative',
            '&:hover::before': {
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundColor: tokens.statePrimaryHoverStateLayerColor
            }
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(26, 26, 26, 0.12)'
          }
        }
      }
    }
  },
  MuiTab: {
    root: {
      textTransform: 'initial'
    }
  },
  MuiDialogTitle: {
    root: {
      paddingTop: appTheme.spacingSize.xl,
      paddingBottom: appTheme.spacingSize.xl,
      color: tokens.colorOnSurfaceVariant,
      '& [data-test="modal__subtitle"]': {
        color: tokens.colorOnSurfaceVariant
      }
    }
  },
  MuiDialogContent: {
    root: {
      paddingTop: 0,
      paddingBottom: appTheme.spacingSize.xl
    }
  },
  MuiDialogActions: {
    root: {
      padding: appTheme.spacingSize.xl,
      paddingTop: 0,
      '& button.MuiButtonBase-root': {
        marginLeft: appTheme.spacingSize.l
      }
    }
  },
  MuiCheckbox: {
    root: {
      color: tokens.colorOutline
    },
    colorPrimary: {
      '&$checked': {
        color: tokens.colorPrimary
      }
    }
  },
  MuiIconButton: {
    root: {}
  },
  MuiChip: {
    icon: {},
    avatar: { color: tokens.paletteNeutral99 },
    root: {
      backgroundColor: tokens.colorSurfaceVariant,
      borderRadius: 10,
      '& .MuiChip-avatar': {
        color: tokens.paletteNeutral99
      }
    }
  },
  MuiFormHelperText: {
    root: {
      color: tokens.colorOnSurfaceVariant
    }
  },
  MuiFormControlLabel: {
    label: {
      fontSize: appTheme.smallFontSize,
      fontWeight: 500
    },
    root: {
      '&.dense ': {
        marginLeft: -4,
        '& .MuiButtonBase-root': {
          padding: 2
        },
        '& .MuiFormControlLabel-label': {
          paddingLeft: 7
        }
      }
    }
  },

  MuiButton: {
    root: {
      color: tokens.colorOnSurface,
      textTransform: 'none',
      transition: 'border-color background-color 250ms ease 0ms',
      position: 'relative',
      '& .MuiButton-label': {
        transition: 'color 250ms ease 0ms'
      },
      fontWeight: 400,
      // base on variant Button use diff padding. It is problematic with active state.
      // It is important to keep the same padding
      padding: '6px 16px',
      '&:not(.MuiButton-outlined)': {
        borderRadius: '4px'
      },
      // MuiButton doesn't have active state and in theory we shouldn't use Buttons with active state
      // But it is too late for now (?)
      // Code below set active state on buttons without setting specific colors
      // In the &:after is set backdrop-filter which made background color darker
      // At the same time StyledMuiButton has MuiButton-label with z-index:1, so filter isn't applied to the label
      '&[data-test-active="true"]': {
        '&:after': {
          // Note '""' (double quote) is required here. Otherwise content won't render correctly
          content: "''",
          display: 'block',
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          borderRadius: '4px',
          backdropFilter: 'brightness(0.9)'
        }
      },
      '&[data-test-active="true"] .MuiButton-label': {
        zIndex: 1
      },
      '&[data-action-list-button]': {
        borderColor: tokens.colorSurfaceVariant,
        justifyContent: 'space-between',
        borderRadius: 0,
        paddingLeft: 8,
        '& span': {
          fontWeight: 500,
          fontSize: 13
        },
        '&:hover': {
          background: '#fff',
          borderColor: tokens.colorSurfaceVariant
        },
        '&:focus': {
          background: '#fff',
          borderColor: tokens.colorPrimary
        }
      },
      '& .loading-icon': {
        height: 24,
        width: 24,
        lineHeight: `26px`
      }
    },
    sizeSmall: {
      '& .loading-icon': {
        lineHeight: `28px`
      }
    },
    text: {
      // base on variant Button use diff padding. It is problematic with active state.
      // It is important to keep the same padding
      padding: '6px 16px',
      '& .MuiCircularProgress-root': {
        color: tokens.colorOnSurface
      },
      '&.Mui-focusVisible': {
        backgroundColor: `rgba(0, 0, 0, 0.04)`
      }
    },
    textPrimary: {
      color: tokens.colorPrimary,
      '&:hover': {
        backgroundColor: 'none',
        '&::before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: tokens.statePrimaryHoverStateLayerColor
        }
      },
      '&.Mui-focusVisible': {
        backgroundColor: tokens.statePrimaryHoverStateLayerColor
      },
      '&:active&:hover': {
        backgroundColor: 'none',
        '&::before': {
          backgroundColor: tokens.statePrimaryPressedStateLayerColor
        }
      },
      '&$disabled': {
        color: tokens.stateOnSurfaceDisabledContentStateLayerColor
      },
      '& .MuiCircularProgress-root': {
        color: tokens.colorPrimary
      }
    },
    textSecondary: {
      color: tokens.colorAlert,
      '&:hover': {
        backgroundColor: 'none',
        '&::before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: tokens.stateAlertHoverStateLayerColor
        }
      },
      '&.Mui-focusVisible': {
        backgroundColor: tokens.stateAlertPressedStateLayerColor
      },
      '&:active&:hover': {
        backgroundColor: 'none',
        '&::before': {
          backgroundColor: tokens.stateAlertPressedStateLayerColor
        }
      },
      '&$disabled': {
        color: tokens.stateOnSurfaceDisabledContentStateLayerColor
      },
      '& .MuiCircularProgress-root': {
        color: tokens.colorAlert
      }
    },
    contained: {
      boxShadow: 'none',
      backgroundColor: tokens.colorSurfaceVariant,
      color: tokens.colorOnSurface,

      '&:hover': {
        backgroundColor: tokens.colorSurfaceVariant,
        boxShadow: tokens.elevationLevel1
      },
      '&:focused': {
        boxShadow: 'none',
        backgroundColor: tokens.colorSurfaceVariant
      },
      '&:active&:hover': {
        boxShadow: tokens.elevationLevel0,
        backgroundColor: tokens.colorSurfaceVariant
      },
      '& .MuiCircularProgress-root': {
        color: tokens.colorOnSurface
      }
    },
    containedPrimary: {
      backgroundColor: tokens.colorPrimary,
      color: tokens.colorOnPrimary,
      '&:hover': {
        backgroundColor: tokens.colorPrimary,
        boxShadow: tokens.elevationLevel1,
        '&::before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: tokens.stateOnPrimaryHoverStateLayerColor
        }
      },
      '&:focused': {
        backgroundColor: tokens.colorPrimary
      },
      '&:active&:hover': {
        backgroundColor: tokens.colorPrimary,
        boxShadow: tokens.elevationLevel0,
        '&::before': {
          backgroundColor: tokens.stateOnPrimaryPressedStateLayerColor
        }
      },
      '&$disabled': {
        backgroundColor: tokens.stateOnSurfaceDisabledContainerStateLayerColor,
        color: tokens.stateOnSurfaceDisabledContentStateLayerColor,
        '& .MuiCircularProgress-root': {
          color: tokens.stateOnSurfaceDisabledContentStateLayerColor
        }
      },
      '& .MuiCircularProgress-root': {
        color: tokens.colorOnPrimary
      }
    },
    containedSecondary: {
      backgroundColor: tokens.colorAlert,
      color: tokens.colorOnAlert,
      '&:hover': {
        backgroundColor: tokens.colorAlert,
        boxShadow: tokens.elevationLevel1,
        '&::before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: tokens.stateOnAlertHoverStateLayerColor
        }
      },
      '&:focused': {
        backgroundColor: tokens.colorAlert
      },
      '&:active&:hover': {
        backgroundColor: tokens.colorAlert,
        boxShadow: tokens.elevationLevel0,
        '&::before': {
          backgroundColor: tokens.stateOnAlertPressedStateLayerColor
        }
      },
      '&$disabled': {
        backgroundColor: tokens.stateOnSurfaceDisabledContainerStateLayerColor,
        color: tokens.stateOnSurfaceDisabledContentStateLayerColor,
        '& .MuiCircularProgress-root': {
          color: tokens.stateOnSurfaceDisabledContentStateLayerColor
        }
      },
      '& .MuiCircularProgress-root': {
        color: tokens.colorOnAlert
      }
    },
    outlined: {
      borderColor: tokens.colorOutline,
      '& .MuiCircularProgress-root': {
        color: tokens.colorOnSurface
      },
      '&.Mui-focusVisible': {
        backgroundColor: `rgba(0, 0, 0, 0.04)`
      }
    },
    outlinedPrimary: {
      color: tokens.colorPrimary,
      borderColor: tokens.colorOutline,
      '&:active&:hover': {
        background: 'none',
        '&::before': {
          backgroundColor: tokens.statePrimaryPressedStateLayerColor
        }
      },
      '&:hover': {
        background: 'none',
        borderColor: tokens.colorOutline,
        '&::before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: tokens.statePrimaryHoverStateLayerColor
        }
      },
      '&.Mui-focusVisible': {
        backgroundColor: tokens.statePrimaryHoverStateLayerColor
      },
      '&$disabled': {
        borderColor: tokens.stateOnSurfaceDisabledContainerStateLayerColor,
        color: tokens.stateOnSurfaceDisabledContentStateLayerColor
      },
      '& .MuiCircularProgress-root': {
        color: tokens.colorPrimary
      }
    },
    outlinedSecondary: {
      color: tokens.colorAlert,
      borderColor: tokens.colorOutline,
      '&:hover': {
        borderColor: tokens.colorOutline,
        backgroundColor: 'none',
        '&::before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: tokens.stateAlertHoverStateLayerColor
        }
      },
      '&.Mui-focusVisible': {
        backgroundColor: tokens.stateAlertHoverStateLayerColor
      },
      '&:active&:hover': {
        backgroundColor: 'none',
        '&::before': {
          content: '""',
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: tokens.stateAlertPressedStateLayerColor
        }
      },
      '&$disabled': {
        borderColor: tokens.stateOnSurfaceDisabledContainerStateLayerColor,
        color: tokens.stateOnSurfaceDisabledContentStateLayerColor
      },
      '& .MuiCircularProgress-root': {
        color: tokens.colorAlert
      }
    }
  },
  MuiPaginationItem: {
    root: {
      '&.MuiPaginationItem-ellipsis': {
        color: tokens.colorOnSurface
      },
      '&.MuiButtonBase-root': {
        color: tokens.colorOnSurface,
        '&:hover': {
          backgroundColor: 'none',
          '&::before': {
            content: '""',
            width: '100%',
            height: '100%',
            position: 'absolute',
            backgroundColor: tokens.statePrimaryHoverStateLayerColor
          }
        },
        '&:focused': {
          backgroundColor: 'none'
        },
        '&:active&:hover': {
          backgroundColor: 'none',
          '&::before': {
            backgroundColor: tokens.statePrimaryPressedStateLayerColor
          }
        },
        '&.Mui-selected': {
          color: tokens.stateOnSurfaceDisabledContentStateLayerColor,
          '&.Mui-disabled': {
            color: tokens.stateOnSurfaceDisabledContentStateLayerColor
          }
        }
      }
    }
  },
  MuiTooltip: {
    tooltip: {
      ...ELEVATION.level2,
      margin: 8,
      fontSize: `${tokens.source.bodySmallFontSize}px`,
      fontWeight: parseInt(tokens.source.bodySmallFontWeight, 10),
      lineHeight: `${tokens.source.bodySmallLineHeight}px`,
      letterSpacing: `${tokens.source.bodySmallLetterSpacing}px`,
      color: tokens.colorInverseOnSurface,
      backgroundColor: tokens.colorInverseSurface,
      '&.variant-light': {
        color: tokens.colorOnSurface,
        backgroundColor: tokens.colorSurfaceVariant,

        '& *:not(.MuiTooltip-arrow)': {
          color: `${tokens.colorOnSurface} !important`
        },
        '& .MuiTooltip-arrow': {
          color: tokens.colorSurfaceVariant
        }
      },
      '& *:not(.MuiTooltip-arrow)': {
        textDecoration: 'unset !important',
        color: `${tokens.colorInverseOnSurface} !important`,
        backgroundColor: 'unset !important',
        padding: 'unset !important',
        margin: 'unset !important'
      }
    },
    arrow: {
      color: tokens.colorInverseSurface
    }
  }
};

export default theme;
export { type Theme };

type LabOverrides = {
  [Name in keyof LabComponentNameToClassKey]?: Partial<StyleRules<LabComponentNameToClassKey[Name]>>;
};
